import {Link, useLocation} from 'react-router';
import {useEffect, useState} from 'react';
import {loadLatestRelease as loadRelease, type ReleaseNote} from '@api/releases';
import {MarkdownContent} from './MarkdownContent';
import {useTranslation} from 'react-i18next';

import {
    hasSeenReleaseNotification,
    markReleaseNotificationSeen,
    releaseNotificationSeenEvent,
} from '@utils/releaseNotes';

const ReleaseNotification = () => {
    const {t} = useTranslation();
    const {pathname} = useLocation();
    const [release, setRelease] = useState<ReleaseNote | null>(null);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        let active = true;
        let loading = false;

        const loadLatestRelease = async () => {
            if (loading || document.visibilityState === 'hidden') {
                return;
            }

            loading = true;
            try {
                const data = await loadRelease();

                if (!active) {
                    return;
                }

                if (!data || hasSeenReleaseNotification(data)) {
                    setRelease(null);
                    setOpen(false);
                } else {
                    setRelease(data);
                }
            } catch (error: unknown) {
                console.error('Could not load release information', error);
            } finally {
                loading = false;
            }
        };

        const checkRelease = () => {
            void loadLatestRelease();
        };

        checkRelease();
        window.addEventListener('focus', checkRelease);
        document.addEventListener('visibilitychange', checkRelease);
        const interval = window.setInterval(checkRelease, 60_000);

        return () => {
            active = false;
            window.removeEventListener('focus', checkRelease);
            document.removeEventListener('visibilitychange', checkRelease);
            window.clearInterval(interval);
        };
    }, [pathname]);

    useEffect(() => {
        const syncSeenRelease = () => {
            if (release && hasSeenReleaseNotification(release)) {
                setRelease(null);
                setOpen(false);
            }
        };

        window.addEventListener(releaseNotificationSeenEvent, syncSeenRelease);
        window.addEventListener('storage', syncSeenRelease);
        return () => {
            window.removeEventListener(releaseNotificationSeenEvent, syncSeenRelease);
            window.removeEventListener('storage', syncSeenRelease);
        };
    }, [release]);

    const closePopup = () => {
        if (release) {
            markReleaseNotificationSeen(release);
        }

        setOpen(false);
        setRelease(null);
    };

    if (!release || pathname === '/release-notes') {
        return null;
    }

    return (
        <>
            <button
                className="release-notification-button"
                onClick={() => setOpen(true)}
                type="button"
            >
                {t('release_notes.whats_new')}
            </button>

            {open && (
                <div
                    className="release-overlay"
                    onMouseDown={event => {
                        if (event.target === event.currentTarget) {
                            closePopup();
                        }
                    }}
                    role="presentation"
                >
                    <section
                        aria-labelledby="release-title"
                        aria-modal="true"
                        className="release-modal"
                        role="dialog"
                    >
                        <header>
                            <h2 className="release-modal__title" id="release-title">
                                {release.title}
                            </h2>
                            <button
                                aria-label={t('release_notes.close')}
                                className="release-close-button"
                                onClick={closePopup}
                                type="button"
                            >
                                ×
                            </button>
                        </header>

                        <div className="release-notes">
                            <MarkdownContent text={release.content} />
                        </div>

                        <footer>
                            <Link
                                className="btn btn-outline-primary"
                                to="/release-notes"
                                onClick={closePopup}
                            >
                                {t('release_notes.view_all')}
                            </Link>
                            <button className="btn btn-primary" onClick={closePopup} type="button">
                                {t('common.got_it')}
                            </button>
                        </footer>
                    </section>
                </div>
            )}
        </>
    );
};

export default ReleaseNotification;
